export const name = '$gzGo';

export const taskState = {
  NEW: 'new',
  ACTIVE: 'active'
};

export const taskStatus = {
  DONE: 'done',
  CANCELLED: 'cancelled'
};

/**
 * @ngdoc service
 * @name gazelle.components.go.service:$gzGo
 *
 * @description
 * The $gzGo service provides a mechanism to navigate to a context, interacting with the portal's "go" API.
 *
 * **Cross-Application Tasks:**
 * Cross-Application Tasks are intended to feel like an extension of the calling app (similar to a modal dialog)
 *
 * Calling Application - Application that launches cross-application task
 * - Must support a goContext for the page to return to
 * - Calls $gzGo.startTask to start the cross-application task
 * -- Provides return goContext for the page to return to (which can be different than the actual page that launches)
 * -- Provides a Javascript object payload containing any data to be conveyed to cross-application task as a parameter to $gzGo.startTask
 * - Can use $gzGo.getReturnData to process any data returned from Task Application
 *
 * Task Application - Application that owns the cross-application task
 * - Must support a goContext for the page to return to
 * - Configures gz-page-header directive to support task
 * -- (REQUIRED) Set 'support-task="true"' to enable the page header to switch between Task Mode and Normal Mode
 * -- Set 'support-full-app="true"' to enable the user to show/hide the full app navigation
 * -- Set 'task-title="{{app.cat.key | translate}}"' or 'task-title="{{app.cat.key | translate:[parameters]}}"' to override the page title when in task mode
 * -- (REQUIRED) Set 'task-submit="controller.submit"' to specify a function reference in your controller that returns a promise that returns the payload to pass to the Calling Application
 * -- Set 'task-submit-title="{{app.cat.key | translate}}"' to override the 'Submit' button name
 * -- Set 'task-cancel="controller.cancel"' to specify a function reference in your controller that returns a promise
 * -- Set 'task-disable-submit="controller.disableSubmit"' to specify a function reference in your controller that returns a boolean to control the disabled state of the submit button
 * - Modifies controller on page to support task
 * -- Add appropriate methods to controller to support gz-page-header configuration
 * -- Can use $gzGo.getTaskData to process any data passed into the task from Calling Application
 * -- Page controller/content may need to be modified to support Task Mode and Normal Mode
 *
 */

export default function $gzGo(
  $q,
  $log,
  rnHttp,
  rnApplication,
  $window,
  $state,
  $gzAppBroker,
  gzL10nInterpolationService,
  $rootScope,
  rnPreferences
) {
  //noinspection BadExpressionStatementJS
  'ngInject';

  const TASK_CATEGORY = 'pos.gz.task';
  const TASK_IN_KEY = 'pos.gz.taskIn';
  const TASK_OUT_KEY = 'pos.gz.taskOut';

  let taskData;
  let returnData;
  let isFullApp = false;
  let activatePromise;
  let lastPreferences;

  var unitTest = {
    setTaskData: setTaskData,
    setReturnData: setReturnData,
    setIsFullApp: setIsFullApp
  };

  var service = {
    toContext: toContext,
    taskIsActive: taskIsActive,
    getTaskId: getTaskId,
    startTask: startTask,
    completeTask: completeTask,
    cancelTask: cancelTask,
    exitTask: exitTask,
    goToTaskHome: goToTaskHome,
    showAppNav: showAppNav,
    setAppNav: setAppNav,
    toggleAppNav: toggleAppNav,
    getTaskData: getTaskData,
    getReturnData: getTaskReturnData,
    clearReturnData: clearTaskReturnData,
    unitTest: unitTest
  };

  activate();
  return service;

  function activate() {
    if (activatePromise) {
      return activatePromise.promise;
    }

    activatePromise = $q.defer();
    $rootScope.$on('$stateChangeSuccess',function(event, toState, toParams, fromState, fromParams) {
      rnPreferences.getAll().then(prefs => {
        if (prefs[TASK_CATEGORY] && prefs[TASK_CATEGORY][TASK_IN_KEY]) {
          taskData = rnPreferences.getPrefObjectHelper(prefs, TASK_CATEGORY, TASK_IN_KEY);
          if (taskData) {
            if (taskData.taskState === taskState.NEW) {
              isFullApp = false;
              // This is the task destination
              taskData.taskId = toState.name;
              taskData.toParams = toParams;
              taskData.taskState = taskState.ACTIVE;
              taskData.toAppId = rnApplication.getCurrentAppId();
              taskData.toStateName = toState.name;
              taskData.requestPayload = taskData.payload;
              rnPreferences.setSingleObject(TASK_CATEGORY, TASK_IN_KEY, taskData).catch(error => {
                //catch this error so we don't stop routing
                //if this happens we will still go to destination page, we just won't initiate task
                console.log('$gzGo: Could not initiate task', error);
              });
            }
            else {
              if (rnApplication.getCurrentAppId() !== taskData.toAppId || (rnApplication.getCurrentAppId() === taskData.toAppId && taskData.toStateName !== toState.name && !isFullApp)) {
                rnPreferences.deleteSingle(TASK_CATEGORY, TASK_IN_KEY).catch(error => {
                  //catch this error so we don't stop routing
                  //This could have some odd side effects depending on the situation
                  console.log('$gzGo: Could not clean up task', error);
                });
                taskData = undefined;
              }
            }
          }
        }

        let test = rnPreferences.getPrefObjectHelper(prefs, TASK_CATEGORY, TASK_OUT_KEY);
        if (test) {
          setReturnData(test);
        }

        activatePromise.resolve();
      })
        .catch(error => {
          //catch this error so we don't stop routing
          //If this happens we won't do anything with the task (initiate or clean up)
          console.log('$gzGo: Could not read task data', error);
        });
    });

    return activatePromise.promise;
  }

  //
  // Public functions
  //

  function showAppNav() {
    return (!taskData || (taskData && isFullApp));
  }

  /**
   * @ngdoc function
   * @name taskIsActive
   * @methodOf gazelle.components.go.service:$gzGo
   * @description Check and see if task is active for current page
   * @returns {Promise.<boolean>} promise to a flag indicating if task is active for current page
   */
  function taskIsActive() {
    let deferred = $q.defer();
    activate().then(() => {
      let active = taskData !== undefined && taskData !== null && taskData.taskState === taskState.ACTIVE;
      deferred.resolve(active);
    });
    return deferred.promise;
  }

  function setAppNav(show) {
    isFullApp = show;
  }

  function toggleAppNav() {
    isFullApp = !isFullApp;
  }

  /**
   * @ngdoc function
   * @name getTaskId
   * @methodOf gazelle.components.go.service:$gzGo
   * @description Returns taskId used for the current task (matches incoming route)
   * @returns {object} A javascript object
   */
  function getTaskId() {
    return taskData.taskId;
  }

  /**
   * @ngdoc function
   * @name getTaskData
   * @methodOf gazelle.components.go.service:$gzGo
   * @description Returns taskData for the Active or Paused task
   * @returns {object} A javascript object
   * @example
   * ```
   * {
   *   context: "device-add",
   *   target: "null",
   *   returnContext: "home",
   *   returnTarget: "null",
   *   taskState: "active",
   *   appId: "home",
   *   toAppId: "account",
   *   toStateName: "device-add"
   *   payload: {
   *     somekey: "somevalue"
   *   }
   * }
   * ```
   */
  function getTaskData() {
    return taskData;
  }

  /**
   * @ngdoc function
   * @name getTaskData
   * @methodOf gazelle.components.go.service:$gzGo
   * @description Returns taskData for the Active or Paused task
   * @returns {object} A javascript object
   * @example
   * ```
   * {
   *   context: context,
   *   target: target,
   *   taskState: "new",
   *   status: "done",
   *   payload: payload
   *   payload: {
   *     somekey: "somevalue"
   *   }
   * }
   * ```
   */
  function getTaskReturnData() {
    return returnData;
  }

  function clearTaskReturnData() {
    rnPreferences.deleteSingle(TASK_CATEGORY, TASK_OUT_KEY);
    returnData = undefined;
  }

  /**
   * @ngdoc function
   * @name startTask
   * @methodOf gazelle.components.go.service:$gzGo
   * @description Go to a context as a task. If the context is in the current app, the location will be switched
   * without reloading the app.
   * @param {string} goContext - The context to switch to for the task
   * @param {string} [target] - The optional target (null if not necessary)
   * @param {string} returnContext - The context to return to when the task is complete
   * @param {string} returnTarget - The optional target on return (null if not necessary)
   * @param {object} payload - Javascript object containing any data to be conveyed to the task application
   */
  function startTask(goContext, goTarget, returnContext, returnTarget, payload) {
    let data = {
      context: goContext,
      target: goTarget,
      returnContext: returnContext,
      returnTarget: returnTarget,
      payload: payload,
      taskState: taskState.NEW,
      appId: rnApplication.getCurrentAppId()
    };

    let promises = [];
    promises.push(rnPreferences.deleteSingle(TASK_CATEGORY, TASK_OUT_KEY));
    promises.push(rnPreferences.setSingleObject(TASK_CATEGORY, TASK_IN_KEY, data));
    $q.all(promises).finally(() => {
      toContext(goContext, goTarget);
    });
  }

  function completeTask(payload) {
    if (taskData) {
      let context = taskData.returnContext;
      let target = taskData.returnTarget;
      if (!payload) {
        payload = {};
      }


      let data = {
        context: context,
        target: target,
        taskState: taskState.NEW,
        status: taskStatus.DONE,
        payload: payload,
        requestPayload: taskData.requestPayload
      };

      let promises = [];
      promises.push(rnPreferences.deleteSingle(TASK_CATEGORY, TASK_IN_KEY));
      promises.push(rnPreferences.setSingleObject(TASK_CATEGORY, TASK_OUT_KEY, data));
      $q.all(promises).finally(() => {
        toContext(taskData.returnContext, taskData.returnTarget);
      });
    }
  }

  function cancelTask(payload) {
    if (taskData) {
      let context = taskData.returnContext;
      let target = taskData.returnTarget;
      if (!payload) {
        payload = {};
      }

      let data = {
        context: context,
        target: target,
        taskState: taskState.NEW,
        status: taskStatus.CANCELLED,
        payload: payload,
        requestPayload: taskData.requestPayload
      };

      let promises = [];
      promises.push(rnPreferences.deleteSingle(TASK_CATEGORY, TASK_IN_KEY));
      promises.push(rnPreferences.setSingleObject(TASK_CATEGORY, TASK_OUT_KEY, data));
      $q.all(promises).finally(() => {
        toContext(taskData.returnContext, taskData.returnTarget);
      });
    }
  }

  function exitTask() {
    isFullApp = true;
    rnPreferences.deleteSingle(TASK_CATEGORY, TASK_IN_KEY);
    taskData = undefined;
  }

  function goToTaskHome() {
    $state.go(taskData.taskId, taskData.toParams);
  }

  /**
   * @ngdoc function
   * @name toContext
   * @methodOf gazelle.components.go.service:$gzGo
   * @description Go to a context. If the context is in the current app, the location will be switched
   * without reloading the app.
   * @param {string} context - The context
   * @param {string} [target] - The optional target
   */
  function toContext(context, target) {
    $log.debug('gzGo toContext for context: ' + context + ' target: ' + target);

    return rnHttp.get('portal_service', '/go/contexts', {cache:true}).then(response => {
        // Look for a matching context
        let goContext = _.find(response.data, {name: context});
        if (goContext) {
          // Change to the new location
          if (goContext.ui && goContext.ui.show) {
            // Just show the (mini) app
            //$log.debug("show mini app via app-broker: " + goContext.ui.show);
            $gzAppBroker.show(goContext.ui.show, target);
          }
          else if (rnApplication.getCurrentAppId() === goContext.appId) {
            // If same app, see if we can change the state within that app
            if (goContext.ui && goContext.ui.state) {
              if (goContext.ui.stateParam) {
                let stateParam = {};
                stateParam [goContext.ui.stateParam] = target;
                $state.go(goContext.ui.state, stateParam);
              }
              else {
                $state.go(goContext.ui.state);
              }
            }
            else
            {
              $log.debug("state change not implemented for context: " + context + ". Load it instead.");
              loadApp(goContext, target);
            }
          }
          else {
            // If different app, load the new app
            loadApp(goContext, target);
          }
        }
        else {
          $log.debug("gzGo context not found for " + context);
        }
      })
      .catch(function() {
        $log.debug("gzGo go/contexts API error");
      });

  }

  //
  // Private functions
  //
  function loadApp(goContext, target) {
    let path = gzL10nInterpolationService.interpolate(goContext.path, [target]);
    let href = goContext.appUrl + path;
    $log.debug("loadApp load href: " + href);
    $window.location.href = href;
  }

  function setTaskData(td) {
    taskData = td;
  }

  function setReturnData(rd) {
    returnData = rd;
  }

  function setIsFullApp(isFull) {
    isFullApp = isFull;
  }
} //$gzGo



// WEBPACK FOOTER //
// ../~/@printos/gazelle/src/components/go/go.service.js